import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, User } from '../../lib/lucide-proxy';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../ui/Button';
import { supabase } from '../../config/supabase';

const BlogHighlights = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select(`
          id,
          title,
          slug,
          excerpt,
          featured_image,
          created_at,
          author,
          category:blog_categories(name)
        `)
        .eq('is_published', true)
        .order('created_at', { ascending: false })
        .limit(3);

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error('Error fetching blog highlights:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="animate-pulse flex flex-col items-center">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-12"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-96 bg-gray-100 rounded-2xl"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (posts.length === 0) return null;

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-end mb-12">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-dark-900 mb-4">
              Últimas do Blog
            </h2>
            <p className="text-lg text-dark-600 max-w-2xl">
              Fique por dentro das novidades do mundo cervejeiro.
            </p>
          </motion.div>
          <div className="hidden md:block">
            <Button variant="outline" onClick={() => navigate('/blog')}>
              Ver Todo o Blog
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((post, index) => (
            <motion.article 
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group cursor-pointer h-full flex flex-col"
            >
              <Link to={`/blog/${post.slug}`} className="h-full flex flex-col">
                <div className="relative overflow-hidden rounded-2xl mb-4 aspect-video">
                  <img 
                    src={post.featured_image || 'https://images.unsplash.com/photo-1608270586620-248524c67de9?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'} 
                    alt={post.title} 
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" 
                  />
                  {post.category && (
                    <div className="absolute top-4 left-4">
                      <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-primary-600 uppercase tracking-wider shadow-sm">
                        {post.category.name}
                      </span>
                    </div>
                  )}
                </div>

                <div className="space-y-3 flex-1 flex flex-col">
                  <div className="flex items-center text-sm text-dark-500 space-x-4">
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(post.created_at)}</span>
                    </div>
                    {post.author && (
                      <div className="flex items-center space-x-1">
                        <User className="w-4 h-4" />
                        <span>{post.author}</span>
                      </div>
                    )}
                  </div>

                  <h3 className="font-display font-bold text-xl text-dark-900 group-hover:text-primary-500 transition-colors line-clamp-2">
                    {post.title}
                  </h3>

                  <p className="text-dark-600 line-clamp-3 flex-1">
                    {post.excerpt}
                  </p>

                  <div className="flex items-center text-primary-500 font-medium group-hover:text-primary-600 mt-4">
                    Leia mais <ArrowRight className="w-4 h-4 ml-1" />
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>

        <div className="mt-12 text-center md:hidden">
          <Button variant="outline" className="w-full" onClick={() => navigate('/blog')}>
            Ver Todo o Blog
          </Button>
        </div>
      </div>
    </section>
  );
};

export default BlogHighlights;